import { Builder } from '@builder.io/react';
import dynamic from 'next/dynamic';
import { Icon } from '#const';
import { getIconUrl } from '#utils';
import type { BragItem, BragBarVariant } from './types';

const BragBar = dynamic(() => import('./BragBar'));

const DEFAULT_ITEMS: BragItem[] = [
	{
		type: 'image',
		logo: 'https://cdn.builder.io/api/v1/image/assets%2F039564790efd46a2bd9966880285bc7f%2F5a1fb8fe830044c997236f2faa24c644',
	},
	{ type: 'text', richText: '<p><strong>12+ Years</strong></p> <p>Pursuing</p> <p>Justice</p>' },
	{ type: 'text', richText: '<p><strong>80,000+</strong></p><p>People</p><p>Helped</p>' },
	{ type: 'text', richText: '<p><strong>100% Free</strong></p><p>Case</p><p>Evaluation</p>' },
	{
		type: 'image',
		logo: 'https://cdn.builder.io/api/v1/image/assets%2F039564790efd46a2bd9966880285bc7f%2F90911bdd10a747538028532c27c114ca',
	},
];

export const defaultOptions = {
	backgroundColor: '#D3D3D3',
	items: DEFAULT_ITEMS,
	alignment: 'space-around' as const,
	variant: 'default' as BragBarVariant,
	maxImageHeight: 65,
	separateMobileLayout: false,
	mobileLayout: {
		items: DEFAULT_ITEMS,
		alignment: 'space-around' as const,
	},
};

Builder.registerComponent(BragBar, {
	name: 'BragBar',
	inputs: [
		{
			name: 'backgroundColor',
			friendlyName: 'Background Color',
			type: 'color',
			defaultValue: defaultOptions.backgroundColor,
		},
		{
			name: 'borderColor',
			friendlyName: 'Border Color',
			type: 'color',
		},
		{
			name: 'borderWidth',
			friendlyName: 'Border Width',
			type: 'number',
			helperText: 'Border width in pixels.',
		},
		{
			name: 'margin',
			friendlyName: 'Margin',
			type: 'object',
			subFields: [
				{
					name: 'inline',
					friendlyName: 'Horizontal',
					type: 'number',
					helperText: 'Left and right margin in pixels.',
				},
				{
					name: 'block',
					friendlyName: 'Vertical',
					type: 'number',
					helperText: 'Top and bottom margin in pixels.',
				},
			],
		},
		{
			name: 'marginMobile',
			friendlyName: 'Margin - Mobile',
			type: 'object',
			subFields: [
				{
					name: 'inline',
					friendlyName: 'Horizontal',
					type: 'number',
					helperText: 'Left and right margin in pixels. Falls back to desktop value if not set.',
				},
				{
					name: 'block',
					friendlyName: 'Vertical',
					type: 'number',
					helperText: 'Top and bottom margin in pixels. Falls back to desktop value if not set.',
				},
			],
		},
		{
			name: 'items',
			friendlyName: 'Items',
			type: 'list',
			subFields: [
				{
					name: 'type',
					friendlyName: 'Content Type',
					type: 'string',
					enum: ['image', 'text'],
					defaultValue: 'image',
				},
				{
					name: 'logo',
					friendlyName: 'Image',
					type: 'file',
					allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg', 'gif', 'webp'],
					showIf: function (options) {
						return options.get('type') === 'image';
					},
				},
				{
					name: 'richText',
					friendlyName: 'Text',
					type: 'richText',
					showIf: function (options) {
						return options.get('type') === 'text';
					},
				},
				{
					name: 'link',
					friendlyName: 'Link',
					type: 'string',
					showIf: function (options) {
						return options.get('type') === 'image';
					},
				},
			],
			onChange: function (options) {
				if (options.get('items').length > 6) {
					options.set('items', options.get('items').slice(0, 6));
					alert('maximum items is 6, delete items to continue');
				}
			},
			defaultValue: defaultOptions.items,
		},
		{
			name: 'variant',
			friendlyName: 'Variant',
			type: 'string',
			enum: [
				{ label: 'Default', value: 'default' },
				{ label: 'SP', value: 'sp' },
			],
			defaultValue: defaultOptions.variant,
			helperText: 'SP variant spans 9 columns on desktop with centered content.',
		},
		{
			name: 'maxImageHeight',
			friendlyName: 'Max Image Height (Desktop)',
			type: 'number',
			defaultValue: defaultOptions.maxImageHeight,
			helperText: 'Desktop image height in pixels. Only applies to the SP variant.',
			showIf: function (options) {
				return options.get('variant') === 'sp';
			},
		},
		{
			name: 'alignment',
			friendlyName: 'Content Alignment',
			type: 'string',
			enum: [
				{ label: 'Space Around', value: 'space-around' },
				{ label: 'Space Between', value: 'space-between' },
				{ label: 'Space Evenly', value: 'space-evenly' },
				{ label: 'Center', value: 'center' },
				{ label: 'Left', value: 'left' },
				{ label: 'Right', value: 'right' },
			],
			defaultValue: defaultOptions.alignment,
		},
		{
			name: 'separateMobileLayout',
			friendlyName: 'Separate Mobile Layout',
			type: 'boolean',
			defaultValue: defaultOptions.separateMobileLayout,
			helperText:
				'If true, the layout will be different on mobile devices. Click "Refresh your preview" to see the changes.',
		},
		{
			name: 'mobileLayout',
			friendlyName: 'Mobile Layout',
			type: 'object',
			required: false,
			helperText: 'Options for the mobile layout.',
			showIf: function (options) {
				return options.get('separateMobileLayout') === true;
			},
			subFields: [
				{
					name: 'items',
					friendlyName: 'Items',
					type: 'list',
					subFields: [
						{
							name: 'type',
							friendlyName: 'Content Type',
							type: 'string',
							enum: ['image', 'text'],
							defaultValue: 'image',
						},
						{
							name: 'logo',
							friendlyName: 'Image',
							type: 'file',
							allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg', 'gif', 'webp'],
							showIf: function (options) {
								return options.get('type') === 'image';
							},
						},
						{
							name: 'richText',
							friendlyName: 'Text',
							type: 'richText',
							showIf: function (options) {
								return options.get('type') === 'text';
							},
						},
						{
							name: 'link',
							friendlyName: 'Link',
							type: 'string',
							showIf: function (options) {
								return options.get('type') === 'image';
							},
						},
					],
					onChange: function (options) {
						if (options.get('items').length > 6) {
							options.set('items', options.get('items').slice(0, 6));
							alert('maximum items is 6, delete items to continue');
						}
					},
					defaultValue: defaultOptions.mobileLayout.items,
				},
				{
					name: 'alignment',
					friendlyName: 'Content Alignment',
					type: 'string',
					enum: [
						{ label: 'Space Around', value: 'space-around' },
						{ label: 'Space Between', value: 'space-between' },
						{ label: 'Space Evenly', value: 'space-evenly' },
						{ label: 'Center', value: 'center' },
						{ label: 'Left', value: 'left' },
						{ label: 'Right', value: 'right' },
					],
					defaultValue: defaultOptions.mobileLayout.alignment,
				},
			],
		},
	],
	image: getIconUrl(Icon.LOGOBAR),
});
